'use client'

import { useTranslations } from 'next-intl'
import { RefObject, useRef, useState } from 'react'

import Lightbox from '@/app/[locale]/(worlds)/profile/[identifier]/components/Lightbox'
import IconPlay from '@/components/assets/icon_play.svg'

import OptimizedImage from '@/components/OptimizedImage'
import { useLayoutContext } from '@/context/LayoutContext'
import { useFetchBlendVisionVodInfo } from '@/hooks/useFetchBlendVisionVodInfo'
import { usePollBlendVisionVodStatus } from '@/hooks/usePollBlendVisionVodStatus'
import { createDefaultAssets } from '@/lib/asset'
import { cn } from '@/lib/utils'
import { UserProfile } from '@/services/cms'
import { Asset } from '@/types/asset'
import { useIsClient, useMediaQuery, useResizeObserver } from 'usehooks-ts'

type UserAboutWithMediasProps = {
  userProfile: Partial<UserProfile>
}

const MAX_BIO_DISPLAY_LENGTH = 200

function IconPlayButton() {
  return (
    <div className="absolute top-1/2 left-1/2 z-interactive flex h-[54px] w-[54px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[#59667799] backdrop-blur-md transition-all duration-300 md:size-18">
      <IconPlay className="fill-vive-primary-20 size-6" />
    </div>
  )
}

export default function UserAboutWithMedias({ userProfile }: UserAboutWithMediasProps) {
  const t = useTranslations()
  const { headerHeight } = useLayoutContext()
  const { profile_media: medias = [], bio = '' } = userProfile

  const containerRef = useRef<HTMLElement>(null)
  const mediaRef = useRef<HTMLDivElement>(null)
  const [assets, setAssets] = useState<Asset[]>(createDefaultAssets(medias, false))
  const [selectedAssetIndex, setSelectedAssetIndex] = useState(0)
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  const { height: mediaHeight = 0 } = useResizeObserver({
    ref: mediaRef as RefObject<HTMLElement>,
    box: 'border-box',
  })

  const showReadMore = bio.length > MAX_BIO_DISPLAY_LENGTH
  const hasMedia = assets.length > 0
  const hasBio = bio.length > 0
  const isDesktop = useMediaQuery('(min-width: 1024px)')
  const isTablet = useMediaQuery('(min-width: 768px)')
  const isClient = useIsClient()
  const offset = headerHeight + (isDesktop ? 72 : 56)

  useFetchBlendVisionVodInfo(assets, setAssets, 'public')

  usePollBlendVisionVodStatus(assets, setAssets, 'public')

  const handleReadMore = () => {
    setIsExpanded((prev) => !prev)

    if (isExpanded) {
      setTimeout(() => {
        if (containerRef.current) {
          const elementPosition = containerRef.current.getBoundingClientRect().top
          const offsetPosition = elementPosition + window.scrollY - offset
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          })
        }
      }, 100)
    }
  }

  const asset = assets[0]
  const assetImageSrc = asset?.image?.url || asset?.video?.coverImage
  const isVideo = !!asset?.video

  if (!hasMedia && !hasBio) return null

  return (
    <section
      id="about"
      data-component="profile-about"
      ref={containerRef}
      className="w-full scroll-mt-36 px-4 md:px-8 lg:scroll-mt-40 lg:px-18"
    >
      <div
        className={cn('flex flex-1 flex-col-reverse gap-6 md:flex-row md:gap-8', {
          'items-start': hasMedia && hasBio,
        })}
      >
        {hasMedia && (
          <div
            ref={mediaRef}
            className="1.5xl:w-[416px] 1.5xl:h-[244px] relative aspect-358/210 w-full shrink-0 overflow-hidden rounded-2xl bg-black/10 transition-all duration-300 hover:shadow-[0_0_20px_rgba(89,102,119,0.4)] md:sticky md:h-[200px] md:w-[340px]"
            style={{
              top: isClient && isTablet ? offset : undefined,
            }}
          >
            <div
              className="group relative h-full w-full cursor-pointer"
              onClick={() => {
                setSelectedAssetIndex(0)
                setIsLightboxOpen(true)
              }}
            >
              {assetImageSrc ? (
                <OptimizedImage
                  src={assetImageSrc}
                  className="size-full object-cover transition-transform duration-500 hover:scale-105"
                  alt="media preview"
                  fill
                  sizes="(max-width: 767px) calc(100vw - 32px), (max-width: 1535px) 340px, 416px"
                />
              ) : isVideo && asset.video?.url ? (
                <video src={asset.video.url} className="size-full object-cover" muted loop />
              ) : (
                <div className="flex size-full items-center justify-center bg-gray-800 text-gray-500">
                  No Preview
                </div>
              )}

              {isVideo && <IconPlayButton />}

              {assets.length > 1 && (
                <div className="subheader-2 pointer-events-none absolute right-2 bottom-2 rounded-full bg-[#59667799] px-3 py-2 text-sm font-bold text-white backdrop-blur-md">
                  +{assets.length} Media
                </div>
              )}
            </div>
          </div>
        )}

        {hasBio && (
          <div
            className={cn('flex flex-1 flex-col overflow-hidden', {
              'overflow-hidden': !isExpanded && showReadMore && isTablet,
            })}
            style={{
              height:
                !isExpanded && showReadMore && hasMedia && mediaHeight > 0 && isTablet
                  ? mediaHeight
                  : undefined,
            }}
          >
            <h2 className="body-1 md:header-4 mb-4 font-bold text-white md:text-white">
              {t('profile-page.anchors.about')}
            </h2>

            <p
              className={cn(
                'body-2 md:body-1 whitespace-pre-line text-white md:text-white',
                showReadMore && !isExpanded && 'line-clamp-5',
              )}
            >
              {isExpanded || !showReadMore ? bio : bio}
            </p>

            {showReadMore && (
              <button
                type="button"
                className="text-vive-brand-40 md:text-vive-brand-40 body-2 md:body-1 mt-2 cursor-pointer text-left hover:underline"
                onClick={handleReadMore}
              >
                {isExpanded ? t('profile-page.about.read-less') : t('profile-page.about.read-more')}
              </button>
            )}
          </div>
        )}
      </div>

      {isClient && isLightboxOpen && (
        <Lightbox
          assets={assets}
          selectedAssetIndex={selectedAssetIndex}
          setSelectedAssetIndex={setSelectedAssetIndex}
          setIsLightboxOpen={setIsLightboxOpen}
        />
      )}
    </section>
  )
}
